import type React from "react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export type ActiveInput =
  | {
      type: "postComment";
      postId: number;
      autoFocus?: boolean;
      onDraftRestorationSettled?: () => void;
      draftRestorationSurfaceKey?: string;
    }
  | {
      type: "reply";
      postId: number;
      parentId: number;
      anchorId: number;
      autoFocus?: boolean;
      onDraftRestorationSettled?: () => void;
      draftRestorationSurfaceKey?: string;
    }
  | null;

type OpenInputOptions = {
  autoFocus?: boolean;
  onDraftRestorationSettled?: () => void;
  draftRestorationSurfaceKey?: string;
};

interface FeedInputContextType {
  activeInput: ActiveInput;
  inputInteractionRevision: number;
  openPostComment: (postId: number, options?: OpenInputOptions) => void;
  openReply: (
    postId: number,
    parentId: number,
    anchorId: number,
    options?: OpenInputOptions,
  ) => void;
  closeAll: () => void;
  togglePostComment: (postId: number) => void;
  toggleReply: (postId: number, parentId: number, anchorId: number) => void;
}

const FeedInputContext = createContext<FeedInputContextType | undefined>(
  undefined,
);

export const FeedInputProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [activeInput, setActiveInput] = useState<ActiveInput>(null);
  const [inputInteractionRevision, setInputInteractionRevision] = useState(0);
  const recordManualInteraction = useCallback(
    () => setInputInteractionRevision((current) => current + 1),
    [],
  );
  const toValidId = useCallback(
    (value: unknown, label: string): number | null => {
      const num = Number(value);
      if (!Number.isSafeInteger(num) || num <= 0) {
        console.error(`FeedInputContext received invalid ${label}:`, value);
        return null;
      }
      return num;
    },
    [],
  );

  const openPostComment = useCallback(
    (postId: number, options?: OpenInputOptions) => {
      const normalizedPostId = toValidId(postId, "postId");
      if (normalizedPostId === null) return;
      if (options?.autoFocus !== false) recordManualInteraction();
      setActiveInput({
        type: "postComment",
        postId: normalizedPostId,
        autoFocus: options?.autoFocus,
        onDraftRestorationSettled: options?.onDraftRestorationSettled,
        draftRestorationSurfaceKey: options?.draftRestorationSurfaceKey,
      });
    },
    [recordManualInteraction, toValidId],
  );

  const openReply = useCallback(
    (
      postId: number,
      parentId: number,
      anchorId: number,
      options?: OpenInputOptions,
    ) => {
      const normalizedPostId = toValidId(postId, "postId");
      const normalizedParentId = toValidId(parentId, "parentId");
      const normalizedAnchorId = toValidId(anchorId, "anchorId");
      if (!normalizedPostId || !normalizedParentId || !normalizedAnchorId)
        return;
      if (options?.autoFocus !== false) recordManualInteraction();
      setActiveInput({
        type: "reply",
        postId: normalizedPostId,
        parentId: normalizedParentId,
        anchorId: normalizedAnchorId,
        autoFocus: options?.autoFocus,
        onDraftRestorationSettled: options?.onDraftRestorationSettled,
        draftRestorationSurfaceKey: options?.draftRestorationSurfaceKey,
      });
    },
    [recordManualInteraction, toValidId],
  );

  const closeAll = useCallback(() => {
    recordManualInteraction();
    setActiveInput(null);
  }, [recordManualInteraction]);

  const togglePostComment = useCallback(
    (postId: number) => {
      recordManualInteraction();
      setActiveInput((prev) => {
        const normalizedPostId = toValidId(postId, "postId");
        if (normalizedPostId === null) return prev;
        if (prev?.type === "postComment" && prev.postId === normalizedPostId) {
          return null;
        }
        return { type: "postComment", postId: normalizedPostId };
      });
    },
    [recordManualInteraction, toValidId],
  );

  const toggleReply = useCallback(
    (postId: number, parentId: number, anchorId: number) => {
      recordManualInteraction();
      setActiveInput((prev) => {
        const normalizedPostId = toValidId(postId, "postId");
        const normalizedParentId = toValidId(parentId, "parentId");
        const normalizedAnchorId = toValidId(anchorId, "anchorId");
        if (!normalizedPostId || !normalizedParentId || !normalizedAnchorId)
          return prev;
        if (
          prev?.type === "reply" &&
          prev.postId === normalizedPostId &&
          prev.parentId === normalizedParentId &&
          prev.anchorId === normalizedAnchorId
        ) {
          return null;
        }
        return {
          type: "reply",
          postId: normalizedPostId,
          parentId: normalizedParentId,
          anchorId: normalizedAnchorId,
        };
      });
    },
    [recordManualInteraction, toValidId],
  );

  const value = {
    activeInput,
    inputInteractionRevision,
    openPostComment,
    openReply,
    closeAll,
    togglePostComment,
    toggleReply,
  };

  return (
    <FeedInputContext.Provider value={value}>
      {children}
    </FeedInputContext.Provider>
  );
};

export const useFeedInput = () => {
  const context = useContext(FeedInputContext);
  if (context === undefined) {
    throw new Error("useFeedInput must be used within a FeedInputProvider");
  }
  return context;
};
